import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import {
    getProductListDataView,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductGridCard from "@insite/content-library/Widgets/ProductList/ProductListProductGridCard";
import ProductListTable from "@insite/content-library/Widgets/ProductList/ProductListTable";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, ReactNode, useCallback, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
    };
}
const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: { isSearchPage, parameter },
        },
    } = state;
    const {
        websiteSettings: { includeRealTimeInventoryAndPricingForSeo },
        productSettings: { productLoadStyle },
    } = getSettingsCollection(state);
    return {
        isLoading: state.pages.productList.isLoading,
        productsDataView: getProductListDataView(state),
        view: state.pages.productList.view || ownProps.fields.productListDefaultView,
        // searchWithinQuery: state.pages.productList?.filteredApiParameter?.searchWithin,
        searchWithinQuery: state.pages.productList?.productFilters?.searchWithinQueries,
        searchQuery: state.pages.productList.parameter?.search,
        includeRealTimeInventoryAndPricingForSeo,
        isSearchPage,
        productLoadStyle,
        page: parameter?.page ?? 1,
        pageSize: parameter?.pageSize ?? 0,
        totalItemCount: getProductListDataViewProperty(state, "pagination")?.totalItemCount ?? 0,
        isLoadMore: isLoadMore(state),
    };
};

const mapDispatchToProps = {
    setView,
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    nosearchWrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    noProductsInfo?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    loadMoreWrapper?: InjectableCss;
    loadMoreButton?: ButtonPresentationProps;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            margin-bottom: 35px;
            // border-top: 1px solid ${getColor("common.border")};
        `,
    },
    nosearchWrapper: {
        css: css`
            margin-top: 28px;
            margin-bottom: 105px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 50px;
                            margin-bottom: 23px;
                        `,
                        css`
                            margin-top: 50px;
                            margin-bottom: 23px;
                        `,
                        css`
                            margin-top: 28px;
                            margin-bottom: 105px;
                        `,
                        css`
                            margin-top: 28px;
                            margin-bottom: 105px;
                        `,
                        css`
                            margin-top: 28px;
                            margin-bottom: 105px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cardList: {
        gridContainer: {
            gap: 29,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        css: css`
            display: block;
            color: #33333a;
            font-size: 44px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 53px;
            @media all and (max-width: 992px) and (min-width: 0px) {
                font-size: 36px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 43px;
                color: #33333a;
                padding: 0 25px;
            }
        `,
    },
    noProductsInfo: {
        css: css`
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 24px;
            color: #33333a;
            display: block;
        `,
    },
    cardContainerMultiColumnStyles: {
        cardDivider: {
            css: css`
                width: 100%;
                height: 100%;
                position: relative;
            `,
        },
        gridItem: {
            css: css`
                border-bottom: none;
                padding-top: 13.5px;
                padding-bottom: 13.5px;
            `,
        },
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
                margin-bottom: 0px;
                border-top: none;
            `,
        },
        gridItem: {
            css: css`
                border-bottom: none;
                padding: 10px 15px;
            `,
        },
    },
    loadMoreWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    loadMoreButton: {
        variant: "secondary",
        css: css`
            margin: 0 10px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = listStyles;

const ProductListCardList: FC<Props> = ({
    isLoading,
    productsDataView,
    view,
    fields,
    setView,
    productLoadStyle,
    page,
    pageSize,
    totalItemCount,
    isLoadMore,
    searchQuery,
    searchWithinQuery,
    includeRealTimeInventoryAndPricingForSeo,
    id,
    isSearchPage,
    addProductFilters,
}) => {
    React.useEffect(() => {
        setView({ view });
    }, []);

    const observer: React.MutableRefObject<any> = useRef();
    const itemsLeft = totalItemCount - page * pageSize;

    const infiniteScrollTriggerRef = useCallback(
        node => {
            if (productLoadStyle !== "InfiniteScroll" || getIsWebCrawler() || isLoading) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
            }

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && itemsLeft > 0) {
                    addProductFilters({ page: page + 1 });
                }
            });

            if (node) {
                observer.current?.observe(node);
            }
        },
        [productLoadStyle, isLoading, itemsLeft],
    );

    const loadMoreClickHandler = () => {
        addProductFilters({ page: page + 1 });
    };

    if (isLoading && !productsDataView.value && !isLoadMore) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }
    const products = productsDataView.value;

    if (products.length === 0) {
        return (
            <StyledWrapper {...styles.nosearchWrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    <StyledWrapper {...styles.noProductsText}>
                        {translate("No results found for: ")}
                        <StyledWrapper
                            css={css`
                                text-transform: capitalize;
                                display: inline;
                            `}
                        >
                            {translate(
                                `${
                                    searchQuery
                                        ? searchWithinQuery
                                            ? `${searchQuery},${searchWithinQuery?.join(",")}`
                                            : searchQuery
                                        : searchWithinQuery
                                        ? searchWithinQuery?.join(",")
                                        : ""
                                }`,
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.noProductsInfo}>
                            {translate(
                                "You can search again with other product names, category names, or item numbers.  You can also browse our popular product categories below.",
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const renderProducts = (children: ReactNode) => {
        return products.map((product, idx) => (
            <ProductListProductContext product={product} idx={idx} key={product.id}>
                {children}
            </ProductListProductContext>
        ));
    };
    const overriddenProps = { ...fields };
    if (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) {
        overriddenProps.showAvailability = false;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading && !isLoadMore}>
                <Hidden below="md">
                    <CardList extendedStyles={styles.cardList} data-test-selector={`productListCards_${view}`}>
                        {view === "Table" ? (
                            <ProductListTable products={products} {...overriddenProps} />
                        ) : (
                            renderProducts(
                                view === "List" ? (
                                    <CardContainer extendedStyles={styles.cardContainerStyles}>
                                        <ProductListProductCard {...overriddenProps} />
                                    </CardContainer>
                                ) : (
                                    <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                        <ProductListProductGridCard {...overriddenProps} />
                                    </CardContainerMultiColumn>
                                ),
                            )
                        )}
                    </CardList>
                </Hidden>
                <Hidden above="sm">
                    <CardList extendedStyles={styles.cardList} data-test-selector="cardListProductsNarrow">
                        {renderProducts(
                            <CardContainer extendedStyles={styles.cardContainerMultiColumnStyles}>
                                <ProductListProductGridCard {...overriddenProps} />
                            </CardContainer>,
                        )}
                    </CardList>
                </Hidden>
                {!isLoading && isLoadMore && productLoadStyle === "InfiniteScroll" && itemsLeft > 0 && (
                    <div ref={infiniteScrollTriggerRef} data-test-selector="infiniteScrollTrigger"></div>
                )}
                {!isLoading && isLoadMore && productLoadStyle === "LoadMore" && itemsLeft > 0 && (
                    <StyledWrapper {...styles.loadMoreWrapper}>
                        <Button
                            {...styles.loadMoreButton}
                            onClick={loadMoreClickHandler}
                            data-test-selector="loadMoreButton"
                        >
                            {translate("Load More")}
                        </Button>
                    </StyledWrapper>
                )}
                {isLoading && isLoadMore && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCardList),
    definition: {
        group: "Product List",
        displayName: "Card List",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                    {
                        displayName: "Table",
                        value: "Table",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
